/* Native UX polish layer for the Capacitor-wrapped PhoneApp.
 *
 * Adds the feedback loops that a web mockup can't give you on its own:
 *  1. Native taptic-engine impact on every button tap (Capacitor Haptics)
 *  2. Wallpaper parallax — photo translates with scroll for depth
 *  3. Pressed-state amplification — bigger scale + brightness on .phs-row
 *  4. Reveal-on-scroll observer for any card with .phs-reveal class
 *
 * Wires up once on App mount; safe to call repeatedly (idempotent guard). */
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { StatusBar, Style as StatusBarStyle } from '@capacitor/status-bar'

let installed = false

export function installUx() {
  if (installed) return
  installed = true

  // ── Liquid-glass tabbar indicator ──────────────────────────────────
  // Replaces the per-button .phs-tab--active background with a single
  // absolutely-positioned pill that morphs from old to new active tab
  // via Web Animations API. The pill stretches during transit so it
  // visually "flows" between positions like liquid glass — the further
  // it has to travel, the more it elongates at midpoint, then snaps
  // back to a tab-sized rectangle at the end.
  installTabIndicator()

  // Re-install if React unmounts/remounts the tabbar (e.g. on theme
  // change). MutationObserver fires when .phs-tabbar appears.
  new MutationObserver(() => installTabIndicator()).observe(
    document.body, { subtree: true, childList: true }
  )

  // Pull-to-refresh — vertical drag from the top edge of any
  // scrollable phone tab spins our brand loader then replays the screen
  // entry animation. Wallpaper scales 5% during the pull for the iOS
  // "stretching the world" feel.
  installPullToRefresh()

  // Mirror the iOS status-bar text colour to the active phone theme.
  // PhoneApp writes `data-phone-theme` on .sol-phone-screen — when it
  // flips to "dark" the wallpaper goes ink-dark and the system text
  // (time / signal / wifi) needs to flip to light, and vice versa.
  installStatusBarThemeSync()

  // ── Haptics ────────────────────────────────────────────────────────
  // Native taptic engine fires on EVERY button / row / tab / chip tap.
  // Three intensity buckets so the feedback matches the action's
  // gravity:
  //   light    — list rows, tabbar, chevron rows, generic taps
  //   medium   — primary CTAs (Send / Sign / Approve / Convert / Save)
  //   heavy    — destructive (Sign out)
  //
  // Fires on `click` (not on pointerdown / touchstart). A click event in
  // iOS only fires when the user lifted their finger on the same element
  // they pressed without significant movement — exactly the condition for
  // a "real" tap. Our capture-phase tap-suppressor (further below) calls
  // stopImmediatePropagation on click when the finger drifted, so this
  // listener won't fire for swipe/scroll gestures that briefly touched a
  // button. Net result: haptic only on intentional taps, never on
  // accidental swipe-over-row contact.
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null
    if (!t) return
    const btn = t.closest<HTMLElement>(
      'button, [role="button"], .phs-row--button, .phs-tab, .phs-chip, .phs-pay-tile'
    )
    if (!btn) return
    const heavy =
      btn.classList.contains('phs-tx-action--danger') ||
      btn.textContent?.toLowerCase().includes('sign out')
    const medium =
      btn.classList.contains('phs-tx-action--primary') ||
      btn.classList.contains('phs-action-card-cta') ||
      btn.classList.contains('phs-pay-tile--primary') ||
      btn.classList.contains('phs-slide-thumb')
    Haptics.impact({
      style: heavy ? ImpactStyle.Heavy : medium ? ImpactStyle.Medium : ImpactStyle.Light,
    }).catch(() => { /* not on iOS — noop */ })
  })

  // Slide-to-confirm completion → success haptic (a sharper double-tap)
  document.addEventListener('animationend', (e) => {
    const t = e.target as HTMLElement | null
    if (!t) return
    if (t.classList.contains('phs-success-disk')) {
      Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => {})
    }
  })

  // ── Bottom-sheet open / close → soft taptic ────────────────────────
  // Fires when the sheet's slide-in / slide-out animation begins, so the
  // physical pulse precedes the visual movement by ~16ms (feels right).
  document.addEventListener('animationstart', (e) => {
    const t = e.target as HTMLElement | null
    if (!t) return
    const cls = t.className
    if (typeof cls !== 'string') return
    if (
      cls.includes('phs-tx-sheet') ||
      cls.includes('phs-approve-sheet') ||
      cls.includes('phs-profile-sheet') ||
      cls.includes('phs-onboard-sheet') ||
      cls.includes('phs-contract-sheet')
    ) {
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
    }
  })

  // ── Long-press detection (universal) ───────────────────────────────
  // Holding a pressable for >500ms without releasing or moving fires a
  // medium taptic — iOS's "long press recognized" feel. Cancelled if the
  // user lifts or drags away.
  let longPressTimer: number | null = null
  const clearLongPress = () => {
    if (longPressTimer != null) {
      clearTimeout(longPressTimer)
      longPressTimer = null
    }
  }
  document.addEventListener('pointerdown', (e) => {
    const t = e.target as HTMLElement | null
    if (!t) return
    const target = t.closest<HTMLElement>(
      '.phs-row--button, .phs-tab, .phs-chip, .phs-pay-tile, .phs-action-card-cta, ' +
      '.phs-tx-action, .phs-greeting-avatar--button, .phs-customize-row, button'
    )
    if (!target) return
    clearLongPress()
    longPressTimer = window.setTimeout(() => {
      Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {})
      target.classList.add('is-long-pressing')
      longPressTimer = null
    }, 500)
  }, { passive: true })
  const cancelLongPress = () => {
    clearLongPress()
    document.querySelectorAll('.is-long-pressing').forEach((el) =>
      el.classList.remove('is-long-pressing'),
    )
  }
  document.addEventListener('pointerup', cancelLongPress, { passive: true })
  document.addEventListener('pointercancel', cancelLongPress, { passive: true })
  document.addEventListener('pointermove', (e) => {
    // Cancel only on real drag — small jitter shouldn't kill the press.
    if (Math.abs(e.movementX) + Math.abs(e.movementY) > 6) clearLongPress()
  }, { passive: true })

  // ── Wallpaper parallax ──────────────────────────────────────────────
  // The wallpaper photo behind the hero translates upward at half the
  // scroll velocity → adds depth on Home without rebuilding the React
  // tree. Listens for scroll on the active .phs-content node every
  // time the tab changes.
  const installParallax = () => {
    const scroller = document.querySelector<HTMLElement>('.sol-phone-content.phs-content')
    const wallpaper = document.querySelector<HTMLElement>('.phs-screen-wallpaper')
    if (!scroller || !wallpaper) return
    // De-duplicate. The MutationObserver below fires on every DOM
    // mutation (including injecting the PTR loader), and re-running
    // installParallax stacked multiple scroll listeners — each one
    // writing --phs-parallax in rAF — which competed and made the
    // wallpaper visibly jitter during pull-to-refresh. Tag the element
    // and bail if already attached.
    const tagged = scroller as HTMLElement & { __capParallaxInstalled?: boolean }
    if (tagged.__capParallaxInstalled) return
    tagged.__capParallaxInstalled = true
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        wallpaper.style.setProperty('--phs-parallax', `${-scroller.scrollTop * 0.4}px`)
        raf = 0
      })
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
  }
  // Phase trigger — tab content key-remounts on every switch, so we
  // re-attach the listener after the DOM settles each time.
  const observer = new MutationObserver(installParallax)
  observer.observe(document.body, { subtree: true, childList: true })
  installParallax()

  // ── Press-state amplification — REMOVED ─────────────────────────────
  // We no longer apply an `.is-pressing` class on touch start. The
  // class's CSS rules (scale + brightness dim) were firing for any
  // finger that grazed a row during a scroll/swipe — even with motion
  // cancellation there was a brief grey "ghost" visible. The user
  // preferred to disable the visual press feedback entirely; haptic
  // moved to the `click` event (above) where it only fires on a real
  // intentional tap. Long-press (is-long-pressing) still fires after a
  // 500 ms motionless hold — that gesture is deliberate by definition,
  // not a false selection.

  // ── Swipe-vs-tap disambiguation ────────────────────────────────────
  // When the user swipes vertically (PTR, sheet drag, scroll attempt),
  // the touch may have started over a button/row. iOS fires `click` after
  // touchend regardless of how far the finger travelled — so a swipe
  // accidentally activates whatever the touch began on (a wallet account,
  // a tab, a list row). Standard fix: track total finger travel, and if
  // it exceeds the tap threshold, suppress the next click in the capture
  // phase so it never reaches the target's onClick handler.
  let tapStartX = 0
  let tapStartY = 0
  let tapMoved = false
  const TAP_MOVE_THRESHOLD = 10  // px — Apple HIG uses ≤16; 10 is a safe inner radius
  document.addEventListener('touchstart', (e) => {
    if (e.touches.length !== 1) return
    tapStartX = e.touches[0].clientX
    tapStartY = e.touches[0].clientY
    tapMoved = false
  }, { passive: true })
  document.addEventListener('touchmove', (e) => {
    if (tapMoved || e.touches.length < 1) return
    const dx = e.touches[0].clientX - tapStartX
    const dy = e.touches[0].clientY - tapStartY
    if (Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD) {
      tapMoved = true
    }
  }, { passive: true })
  document.addEventListener('click', (e) => {
    if (!tapMoved) return
    // Capture-phase stop before the event reaches React's synthetic
    // event system on the root container. preventDefault for good
    // measure (some elements fire default actions on click — anchors,
    // form submits, etc.).
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()
  }, { capture: true })
}

/// Inserts a single absolutely-positioned indicator into .phs-tabbar
/// and animates it to whichever tab carries .phs-tab--active. Uses
/// Web Animations API so we get a deterministic keyframe sequence
/// (no CSS class-transition timing fights).
function installTabIndicator() {
  const tabbar = document.querySelector<HTMLElement>('.phs-tabbar')
  if (!tabbar) return
  if (tabbar.querySelector('.phs-tab-indicator')) {
    // Already installed — just re-sync to current active tab.
    syncIndicator(tabbar)
    return
  }

  // Position the tabbar as containing block (it already has padding +
  // shadow, position:absolute children fall back to nearest positioned
  // ancestor; we make sure that's the tabbar itself).
  const computed = getComputedStyle(tabbar)
  if (computed.position === 'static') {
    tabbar.style.position = 'relative'
  }

  const indicator = document.createElement('div')
  indicator.className = 'phs-tab-indicator'
  tabbar.prepend(indicator)

  // Observe data-active-tab on .sol-phone-screen (PhoneApp sets this),
  // OR — fallback — observe class changes on each .phs-tab.
  const screen = document.querySelector<HTMLElement>('.sol-phone-screen')
  if (screen) {
    new MutationObserver(() => syncIndicator(tabbar, true))
      .observe(screen, { attributes: true, attributeFilter: ['data-active-tab'] })
  }
  // Also resync on window resize so positions stay correct.
  window.addEventListener('resize', () => syncIndicator(tabbar))

  // First paint — let layout settle then position the pill.
  requestAnimationFrame(() => syncIndicator(tabbar))
}

function syncIndicator(tabbar: HTMLElement, animate = false) {
  const indicator = tabbar.querySelector<HTMLElement>('.phs-tab-indicator')
  const active = tabbar.querySelector<HTMLElement>('.phs-tab.phs-tab--active')
  if (!indicator || !active) return

  // .app-stage applies a non-uniform scale(sx, sy). getBoundingClientRect()
  // reports DEVICE px (post-transform), but the indicator's translateX /
  // width are interpreted in MOCKUP px (pre-transform, since the indicator
  // is a child of .phs-tabbar inside the scaled stage). Undo the X scale
  // so the pill lines up with the active tab on a real device. */
  const stage = tabbar.closest<HTMLElement>('.app-stage')
  const sx = stage
    ? parseFloat(getComputedStyle(stage).getPropertyValue('--phs-scale-x')) || 1
    : 1

  const tabbarBox = tabbar.getBoundingClientRect()
  const activeBox = active.getBoundingClientRect()
  const targetX = (activeBox.left - tabbarBox.left) / sx
  const targetW = activeBox.width / sx

  // Previous position is whatever transform is currently applied.
  const prevTransform = indicator.style.transform
  const prevWidth = indicator.style.width
  const prev = parseTransform(prevTransform)

  // First paint — no animation, just snap.
  if (!animate || prev.x === 0 && !prevWidth) {
    indicator.style.transform = `translateX(${targetX}px)`
    indicator.style.width = `${targetW}px`
    return
  }

  // Distance-aware stretch — the further the pill travels, the more
  // it elongates at midpoint. 4 tabs across the bar → max distance
  // ~ 3 × tab width. We cap at scaleX 1.45 so the stretch never
  // dominates the visual.
  const dist = Math.abs(targetX - prev.x)
  const tabWidth = targetW
  const stretchFactor = Math.min(1 + (dist / tabWidth) * 0.18, 1.45)

  // Hot to keep the pill anchored on both ends: animate translateX
  // AND width with one keyframe sequence. Midpoint width = max possible
  // (covers from old left to new right), then contracts back to target.
  const oldLeft = prev.x
  const newLeft = targetX
  const left = Math.min(oldLeft, newLeft)
  const right = Math.max(oldLeft + parseFloat(prevWidth || `${targetW}`), newLeft + targetW)
  const stretchedWidth = (right - left) * stretchFactor / 1.0

  indicator.animate(
    [
      { transform: `translateX(${oldLeft}px)`, width: `${parseFloat(prevWidth || `${targetW}`)}px` },
      { transform: `translateX(${left}px)`,    width: `${stretchedWidth}px`, offset: 0.55 },
      { transform: `translateX(${newLeft}px)`, width: `${targetW}px` },
    ],
    {
      duration: 420,
      easing: 'cubic-bezier(0.34, 1.42, 0.64, 1)',
      fill: 'forwards',
    },
  ).onfinish = () => {
    indicator.style.transform = `translateX(${newLeft}px)`
    indicator.style.width = `${targetW}px`
    // Tab transition settles → soft taptic so the visual flow has a
    // matching physical "click" at the end of the morph.
    Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
  }
}

function parseTransform(t: string): { x: number } {
  if (!t) return { x: 0 }
  const m = /translateX\(([-\d.]+)px\)/.exec(t)
  return { x: m ? parseFloat(m[1]) : 0 }
}

/// Keeps the iOS / Android system status bar text colour in sync with
/// whatever theme PhoneApp is rendering. PhoneApp writes
/// `data-phone-theme` on `.sol-phone-screen` — when it's "dark" the
/// system needs Style.Light for legibility, otherwise Style.Dark on the
/// cream paper surface. Re-checks once on mount + on every attribute
/// change.
function installStatusBarThemeSync() {
  const apply = () => {
    const screen = document.querySelector<HTMLElement>('.sol-phone-screen')
    const theme = screen?.getAttribute('data-phone-theme')
    const dark = theme === 'dark'
    StatusBar.setStyle({ style: dark ? StatusBarStyle.Light : StatusBarStyle.Dark }).catch(() => {
      /* no-op on non-iOS / non-Android (e.g. web dev) */
    })
  }
  // Wait one frame so React has rendered .sol-phone-screen.
  requestAnimationFrame(apply)
  // Observe the attribute on whichever .sol-phone-screen exists. If it
  // remounts (e.g. on tab key change) we re-attach.
  const attachObserver = () => {
    const screen = document.querySelector<HTMLElement>('.sol-phone-screen')
    if (!screen) return
    const tagged = screen as HTMLElement & { __capStatusBarObserved?: boolean }
    if (tagged.__capStatusBarObserved) return
    tagged.__capStatusBarObserved = true
    new MutationObserver(apply).observe(screen, {
      attributes: true,
      attributeFilter: ['data-phone-theme'],
    })
  }
  requestAnimationFrame(attachObserver)
  new MutationObserver(attachObserver).observe(document.body, {
    childList: true,
    subtree: true,
  })
}

/// Pull-to-refresh — vertical drag from `scrollTop === 0` on the Home
/// tab. Tracks pull progress, shows a chartreuse loader that fills as
/// the user drags, fires a crisp medium haptic when threshold is
/// crossed, then plays our entry animation again as the "refresh"
/// payoff. Mimics iOS pull-to-refresh semantics without firing a real
/// network request (this is a marketing demo wrapper).
function installPullToRefresh() {
  // Threshold = finger-space distance the user must pull to arm refresh.
  // ASYMPTOTE = the max content translate the dampening curve approaches.
  // The curve is `f(x) = x / (1 + x/ASYMPTOTE)` — at x=0 the rate is 1:1
  // (screen feels welded to finger), and as x grows the rate decays so
  // pulling further produces ever-smaller motion. That's iOS rubber-band
  // resistance done in one line: incrementally heavier the further you
  // go, with a hard asymptotic ceiling.
  const THRESHOLD = 72
  // Softer rubber-band: bigger asymptote = the resistance curve flattens
  // more gradually, so the pull feels gentler / less "stiff". Total path
  // still tighter than the original flat 0.85× multiplier but the elastic
  // give in the first ~50 px is more forgiving.
  const ASYMPTOTE = 90
  let startY = 0
  let dragY = 0
  let active = false
  let armed = false
  // Only TOP-edge PTR is JS-driven; bottom-edge bounce is delegated to
  // native iOS rubber-band (via `overscroll-behavior: contain` on the
  // scroller) so it gets proper momentum carry-over from fast upward
  // swipes — physics the JS path can't match. Kept as a single-value
  // type for forward compatibility but only 'top' is currently set.
  let direction: 'top' | null = null
  // Separate, passive bottom-edge tracker: at the bottom of scroll, we
  // do NOT take over the gesture (native handles content bounce) but we
  // DO want to grow the wallpaper proportionally to the upward drag so
  // the visual feedback matches the top PTR. Wallpaper is a sibling of
  // the scroller, so transforming it doesn't conflict with the native
  // bounce on the scroller's compositor layer.
  let bottomActive = false
  let bottomStartY = 0
  // Recent samples of finger position over time, used to estimate
  // release velocity for an inertia-aware snap-back. With a fixed-
  // duration transition, releasing fast feels "wrong" — the spring
  // settles in the same time as a slow release, ignoring the momentum
  // the finger had. We use the last ~80 ms of samples to compute
  // px/ms velocity and lengthen the snap-back proportionally so a
  // yank-release lingers longer than a gentle let-go.
  let velocitySamples: { t: number; y: number }[] = []

  const getScroller = () =>
    document.querySelector<HTMLElement>('.sol-phone-content.phs-content')
  const getStage = () => document.querySelector<HTMLElement>('.app-stage')

  function ensureLoader(stage: HTMLElement): HTMLElement {
    let l = stage.querySelector<HTMLElement>('.cap-ptr-loader')
    if (l) return l
    l = document.createElement('div')
    l.className = 'cap-ptr-loader'
    l.innerHTML = '<div class="cap-ptr-spinner"></div>'
    stage.appendChild(l)
    return l
  }

  // True when a bottom-sheet modal is open — pull-down inside a modal
  // should swipe the sheet down (handled by useSheetSwipe inside the
  // marketing PhoneApp), NOT trigger a screen refresh. Without this
  // guard the swipe-down on a Tx/Invoice/Contract sheet was kicking off
  // PTR — wrong gesture for wrong context.
  const isSheetOpen = () =>
    !!document.querySelector(
      '.phs-tx-sheet:not(.phs-tx-sheet--closing),' +
      ' .phs-approve-sheet:not(.phs-approve-sheet--closing),' +
      ' .phs-onb-sheet:not(.phs-tx-sheet--closing),' +
      ' .s2p-card', // ScanToPay onboarding overlay
    )

  // Touch events (NOT pointer events): on iOS Capacitor WKWebView,
  // pointermove is swallowed by the browser when touch-action allows
  // pan-y, so the loader never appears. Touch events fire even during
  // attempted native scroll, and with `passive: false` on touchmove we
  // can call preventDefault() to take the gesture over from native
  // scroll once we detect a downward pull at scrollTop=0.
  document.addEventListener('touchstart', (e) => {
    if (isSheetOpen()) return
    const s = getScroller()
    if (!s) return
    if (e.touches.length !== 1) return
    // Bottom-edge: track passively so we can grow the wallpaper while
    // the user drags upward + during the native bounce that follows
    // release. Does NOT preventDefault — native still owns the scroll-
    // and-bounce of the content layer.
    if (s.scrollTop + s.clientHeight >= s.scrollHeight - 1) {
      bottomActive = true
      bottomStartY = e.touches[0].clientY
      return
    }
    if (s.scrollTop > 0) return
    direction = 'top'
    startY = e.touches[0].clientY
    dragY = 0
    active = true
    armed = false
    velocitySamples = [{ t: performance.now(), y: startY }]
    // Release the `phsScreenEnter` animation's fill-mode:both LOCK on the
    // scroller's transform. After the tab-mount entry animation finishes,
    // its final keyframe (translateY(0)) stays "locked" as the computed
    // transform with higher cascade priority than inline styles — so our
    // `s.style.transform = translate3d(0, X, 0)` updates during PTR get
    // silently ignored. Setting animation to 'none' here releases the
    // lock; the refresh-replay path further down restores it via the
    // `animation = none / offsetWidth / animation = ''` toggle if needed.
    s.style.animation = 'none'
    // Pin wallpaper parallax to 0 for the duration of the pull, so the
    // photo stays absolutely still while the content slides over it.
    const wallpaper = document.querySelector<HTMLElement>('.phs-screen-wallpaper')
    if (wallpaper) wallpaper.style.setProperty('--phs-parallax', '0px')
  }, { passive: true })

  document.addEventListener('touchmove', (e) => {
    // Bottom-edge wallpaper-zoom path: passive, does not preventDefault
    // so native scroll/bounce keep working. Magnitude uses the same
    // rubber-band curve as top PTR so the visual feedback is symmetric.
    if (bottomActive && e.touches.length >= 1) {
      const wp = document.querySelector<HTMLElement>('.phs-screen-wallpaper')
      const dyUp = bottomStartY - e.touches[0].clientY
      if (dyUp > 0 && wp) {
        const ease = dyUp / (1 + dyUp / ASYMPTOTE)
        const scale = 1 + (ease / ASYMPTOTE) * 0.05
        wp.style.setProperty('--phs-ptr-scale', scale.toFixed(4))
      } else if (dyUp <= 0) {
        // Finger reversed (downward) — release wallpaper back to 1.
        bottomActive = false
        if (wp) wp.style.setProperty('--phs-ptr-scale', '1')
      }
    }
    if (!active) return
    const s = getScroller()
    const stage = getStage()
    if (!s || !stage) return
    const wallpaper = document.querySelector<HTMLElement>('.phs-screen-wallpaper')
    if (e.touches.length < 1) return
    const dy = e.touches[0].clientY - startY
    if (dy <= 0) {
      // Finger reversed direction — going back the way they came.
      // Abort the pull and clean up any transform / loader we'd applied.
      active = false
      s.style.transform = ''
      if (wallpaper) wallpaper.style.setProperty('--phs-ptr-scale', '1')
      const existing = stage.querySelector<HTMLElement>('.cap-ptr-loader')
      if (existing) existing.remove()
      return
    }
    // Take over the gesture from iOS native scroll. Without preventDefault
    // WKWebView keeps trying to scroll the container (capped at the
    // boundary) and the scroll-thread can swallow our transform updates so
    // the content visually doesn't move. Needs `passive: false`.
    if (e.cancelable) e.preventDefault()
    dragY = dy
    // Keep a sliding 80 ms window of (time, y) samples for velocity
    // estimation at touchend. Beyond 80 ms is irrelevant — only the
    // tail of the gesture informs release inertia.
    const now = performance.now()
    velocitySamples.push({ t: now, y: e.touches[0].clientY })
    while (velocitySamples.length > 0 && now - velocitySamples[0].t > 80) {
      velocitySamples.shift()
    }
    // iOS rubber-band: incremental resistance. `f(x) = x / (1 + x/A)` is
    // 1:1 at the start (screen welded to finger), gets heavier as you
    // pull, and asymptotically caps at ASYMPTOTE px regardless of how
    // far the user keeps dragging.
    const eased = dragY / (1 + dragY / ASYMPTOTE)
    s.style.transform = `translate3d(0, ${eased.toFixed(2)}px, 0)`
    if (wallpaper) {
      const ptrScale = 1 + (eased / ASYMPTOTE) * 0.05
      wallpaper.style.setProperty('--phs-ptr-scale', ptrScale.toFixed(4))
    }
    const loader = ensureLoader(stage)
    const progress = Math.min(dragY / THRESHOLD, 1)
    loader.style.setProperty('--cap-ptr-progress', String(progress))
    loader.classList.add('is-arming')
    if (progress >= 1 && !armed) {
      armed = true
      loader.classList.add('is-armed')
      Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
    } else if (progress < 1 && armed) {
      armed = false
      loader.classList.remove('is-armed')
    }
  }, { passive: false })

  const endPull = () => {
    // Bottom-edge cleanup: animate wallpaper back to scale 1. The is-ptr-
    // releasing class gives this transition the same overshoot-free
    // ease-out used by the top-edge spring-back, so the wallpaper
    // settles in sync with the visible end of native iOS bounce.
    if (bottomActive) {
      bottomActive = false
      const wp = document.querySelector<HTMLElement>('.phs-screen-wallpaper')
      if (wp) {
        wp.classList.add('is-ptr-releasing')
        wp.style.setProperty('--phs-ptr-scale', '1')
        window.setTimeout(() => wp.classList.remove('is-ptr-releasing'), 400)
      }
    }
    if (!active) return
    active = false
    const s = getScroller()
    const stage = getStage()
    if (!s || !stage) return
    const loader = stage.querySelector<HTMLElement>('.cap-ptr-loader')
    const wallpaper = document.querySelector<HTMLElement>('.phs-screen-wallpaper')
    // Add the spring-back class so scale → 1 happens with overshoot
    // instead of a hard cut. Removed after the spring settles so future
    // rAF-driven parallax updates aren't eased (which would jitter).
    if (wallpaper) {
      wallpaper.classList.add('is-ptr-releasing')
      window.setTimeout(() => wallpaper.classList.remove('is-ptr-releasing'), 380)
    }

    if (dragY >= THRESHOLD) {
      // Refresh
      Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {})
      // WKWebView quirk: when `transition` and `transform` change in the
      // SAME tick, WebKit often skips the transition and snaps to the new
      // transform instantly. Force a reflow between the two so WebKit
      // commits the current transform as the animation's "from" state.
      s.style.transition = 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)'
      void s.offsetWidth
      // Resting position during spinner phase scales with the new
      // tighter ASYMPTOTE — ~60% of max so the loader pill sits clearly
      // visible while the screen "holds" mid-pull.
      s.style.transform = 'translate3d(0, 32px, 0)'
      // Keep wallpaper at full 5% zoom during the spinner phase — same
      // visual logic as iOS: the world stays "stretched" until the
      // refresh completes.
      if (wallpaper) wallpaper.style.setProperty('--phs-ptr-scale', '1.05')
      if (loader) {
        loader.classList.remove('is-arming', 'is-armed')
        loader.classList.add('is-loading')
      }
      // Spin for 720ms, then snap back + replay the screen entry
      // animation. Toggling the class restarts `phsScreenEnter` so the
      // greeting + wallet + cards re-cascade in like first-paint.
      window.setTimeout(() => {
        // Set the spring-back transition FIRST, force reflow, THEN clear
        // the transform — same WKWebView gotcha as above. The previous
        // order set `transform = ''` before the transition was updated,
        // so WebKit used the stale 0.28s ease-out curve (or snapped
        // instantly), and the spring overshoot never appeared on iOS.
        s.style.transition = 'transform 0.32s cubic-bezier(0.34, 1.42, 0.64, 1)'
        void s.offsetWidth
        s.style.transform = ''
        // Wallpaper springs back from 1.05 → 1 — the .is-ptr-releasing
        // class added in touchend gives this snap its transition.
        if (wallpaper) {
          wallpaper.classList.add('is-ptr-releasing')
          wallpaper.style.setProperty('--phs-ptr-scale', '1')
          window.setTimeout(() => wallpaper.classList.remove('is-ptr-releasing'), 400)
        }
        // Restart the screen entry animation WITHOUT touching the
        // .phs-content class. Removing the class even for one frame
        // dropped mask + overflow + container properties, which made
        // the wallpaper (a sibling outside the scroller) reflow and
        // visibly jitter. Inline `animation: none` overrides the
        // class-defined animation; clearing it lets the class definition
        // re-engage from t=0, so phsScreenEnter plays again while
        // everything else stays stable.
        s.style.animation = 'none'
        void s.offsetWidth
        s.style.animation = ''
        Haptics.impact({ style: ImpactStyle.Light }).catch(() => {})
        if (loader) {
          loader.classList.remove('is-loading')
          loader.classList.add('is-done')
        }
        window.setTimeout(() => {
          if (loader) loader.remove()
          s.style.transition = ''
        }, 380)
      }, 500)
    } else {
      // Snap back without refreshing — inertia-aware.
      // Compute release velocity from the recent samples (px/ms). A fast
      // yank release should produce a longer, softer settle so the
      // spring feels like it's absorbing real momentum; a gentle let-go
      // gets the shorter baseline duration.
      let velocity = 0
      if (velocitySamples.length >= 2) {
        const first = velocitySamples[0]
        const last = velocitySamples[velocitySamples.length - 1]
        const dt = last.t - first.t
        if (dt > 0) velocity = Math.abs((last.y - first.y) / dt)
      }
      // Distance contributes a small baseline too — longer pulls take a
      // bit longer to settle even at zero release velocity.
      const distanceMs = Math.min(dragY, ASYMPTOTE) * 1.4    // up to ~126 ms
      const velocityMs = Math.min(velocity * 160, 320)       // up to +320 ms
      const durationMs = Math.round(420 + distanceMs + velocityMs)
      s.style.transition = `transform ${durationMs}ms cubic-bezier(0.16, 1, 0.3, 1)`
      void s.offsetWidth
      s.style.transform = ''
      if (wallpaper) wallpaper.style.setProperty('--phs-ptr-scale', '1')
      if (loader) {
        loader.classList.remove('is-arming', 'is-armed')
        loader.style.setProperty('--cap-ptr-progress', '0')
        window.setTimeout(() => loader.remove(), 220)
      }
      window.setTimeout(() => { s.style.transition = '' }, durationMs + 40)
    }
    dragY = 0
    armed = false
    direction = null
  }
  document.addEventListener('touchend', endPull)
  // touchcancel fires when iOS interrupts the gesture (e.g. system
  // intercept like a phone call coming in). Treat it as a release so we
  // don't leave the scroller stuck mid-pull.
  document.addEventListener('touchcancel', endPull)
}
